import { useState } from "react";
import { Navigation, Map, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { trackAction } from "../utils/analytics";

const navApps = [
  { 
    id: "google", 
    name: "Google Maps", 
    icon: "https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg",
    getUrl: (lat, lng, name) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=${encodeURIComponent(name)}`
  },
  { 
    id: "apple", 
    name: "Apple Maps", 
    icon: null, // Will use emoji
    getUrl: (lat, lng, name) => `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`
  },
  { 
    id: "waze", 
    name: "Waze", 
    icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Waze_logo.svg/120px-Waze_logo.svg.png",
    getUrl: (lat, lng) => `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`
  },
  { 
    id: "citymapper", 
    name: "Citymapper", 
    icon: null,
    getUrl: (lat, lng, name) => `https://citymapper.com/directions?endcoord=${lat},${lng}&endname=${encodeURIComponent(name)}`
  }
];

export default function DirectionsMenu({ venue, buttonClassName, buttonStyle, buttonSize = "default" }) {
  const [showMenu, setShowMenu] = useState(false);
  const { lat, lng } = venue.location;
  const venueName = venue.name;

  const openDirections = (app) => {
    const url = app.getUrl(lat, lng, venueName);
    window.open(url, '_blank');
    setShowMenu(false);
    trackAction("directions");
  };

  const buttonSizes = {
    small: "p-2",
    default: "p-2.5",
    large: "px-4 py-2.5"
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className={buttonClassName || `${buttonSizes[buttonSize]} rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors flex items-center gap-2 shadow-sm`}
        style={buttonStyle}
        data-testid="directions-button"
      >
        <Navigation className={buttonSize === "small" ? "w-4 h-4" : "w-5 h-5"} />
        {buttonSize === "large" && <span className="font-medium">Get Directions</span>}
      </button>

      <AnimatePresence>
        {showMenu && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowMenu(false)} 
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              className="absolute right-0 top-12 z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-2 min-w-[200px]"
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 mb-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Open with</p>
                <button 
                  onClick={() => setShowMenu(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
              
              {navApps.map((app) => (
                <button
                  key={app.id}
                  onClick={() => openDirections(app)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  data-testid={`directions-${app.id}`}
                >
                  {app.icon ? (
                    <img src={app.icon} alt={app.name} className="w-5 h-5 object-contain" />
                  ) : app.id === "apple" ? (
                    <span className="text-lg">🍎</span>
                  ) : (
                    <Map className="w-5 h-5 text-green-600" />
                  )}
                  <span className="text-sm font-medium text-gray-700">{app.name}</span>
                </button>
              ))}
              
              <div className="mt-1 pt-1 border-t border-gray-100">
                <p className="px-3 py-2 text-xs text-gray-400">
                  {venue.location.address}, {venue.location.city}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
